import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown,
  Sticker,
  Tag,
  DoorOpen,
  Clock,
  Signpost,
  Layers,
  Megaphone,
  ExternalLink,
  CreditCard,
} from "lucide-react";

const SC_STANDARD = [
  { label: "Stickers (Peel & Stick)", icon: Sticker, to: "/shop/tsc?category=stickers", desc: "No transfer tape needed" },
  { label: "Decals (Transfer Tape)", icon: Tag, to: "/shop/tsc?category=transfer-decals", desc: "Windows, trucks, tumblers" },
  { label: "Credit/Debit Card Stickers", icon: CreditCard, to: "/shop/tsc?category=card-stickers", desc: "Card-sized custom vinyl" },
  { label: "Label Sheets", icon: Layers, to: "/shop/tsc?category=label-sheets", desc: "Bottles, jars, packaging" },
  { label: "Door Signs", icon: DoorOpen, to: "/shop/tsc?category=door-signs", desc: "UV-resistant vinyl, eco-solvent ink" },
  { label: "Signage", icon: Signpost, to: "/shop/tsc?category=signage", desc: "Small 7\" × 10\" metal signs" },
];

/**
 * The Sticky Chick "standard line" dropdown.
 * All standard items ship 2-day production before shipping.
 * variant: "nav" | "card"
 */
export default function SCMenu({ variant = "nav", className = "" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  const isCard = variant === "card";

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        data-testid={`sc-menu-toggle-${variant}`}
        className={
          isCard
            ? "btn-brutal btn-brutal-sc w-full sm:w-auto"
            : "label-mono inline-flex items-center gap-1.5 hover:text-sc-pink underline-mono"
        }
      >
        {isCard ? (
          <>
            <Sticker size={16} />
            Shop Sticky Chick Products
            <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
          </>
        ) : (
          <>
            The Sticky Chick
            <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
          </>
        )}
      </button>

      {open && (
        <div
          role="menu"
          data-testid={`sc-menu-panel-${variant}`}
          className={`absolute ${isCard ? "left-0" : "right-0"} top-full mt-2 w-[320px] bg-white border border-black z-[60]`}
          style={{ boxShadow: "6px 6px 0 0 var(--sc-purple)" }}
        >
          <div
            className="border-b border-black px-4 py-3 flex items-center gap-2 text-white"
            style={{
              background:
                "linear-gradient(120deg, var(--sc-purple) 0%, var(--sc-pink) 55%, var(--sc-turquoise) 100%)",
            }}
          >
            <img
              src="/logos/sticky-chick.webp"
              alt="The Sticky Chick"
              className="h-8 w-auto object-contain"
            />
            <div className="flex-1">
              <p className="label-mono text-white text-[10px]">Standard Line</p>
              <p className="font-mono text-xs text-white/95 font-bold">2-Day Production</p>
            </div>
          </div>

          <ul className="p-2">
            {SC_STANDARD.map((opt) => {
              const Icon = opt.icon;
              return (
                <li key={opt.label}>
                  <Link
                    to={opt.to}
                    onClick={() => setOpen(false)}
                    className="flex items-start gap-3 px-3 py-2.5 hover:bg-[color:var(--sc-pink-soft)] transition-colors group"
                    data-testid={`sc-menu-item-${opt.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                  >
                    <Icon size={18} className="mt-0.5 text-sc-purple shrink-0" />
                    <div className="flex-1">
                      <p className="font-mono text-sm font-bold text-sc-purple group-hover:underline">
                        {opt.label}
                      </p>
                      <p className="label-mono text-[color:var(--muted-text)] text-[10px] mt-0.5">
                        {opt.desc}
                      </p>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>

          <div className="border-t border-black px-4 py-3 bg-[color:var(--muted)] flex flex-col gap-2">
            <Link
              to="/shop/tsc?category=marketing"
              onClick={() => setOpen(false)}
              className="flex items-center justify-between label-mono text-sc-purple hover:text-sc-pink"
              data-testid="sc-menu-item-misc-marketing"
            >
              <span className="flex items-center gap-2">
                <Megaphone size={14} /> Misc Marketing Material
              </span>
              <ExternalLink size={14} />
            </Link>
            <Link
              to="/quote"
              onClick={() => setOpen(false)}
              className="flex items-center justify-between label-mono text-black hover:text-sc-pink"
              data-testid="sc-menu-item-other"
            >
              <span>Need something else? Request a quote</span>
              <ExternalLink size={14} />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
